import { useState, useEffect } from "react";
import axiosInstance from "../../redux/helpers/axios";
import Modal from "./Modal";
import CreateCampaignProductForm from "./CreateCampaignProductForm";

const ACCENT_FROM = "#E11D74";
const ACCENT_TO = "#EF4444";

export default function ProductsPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [campaignId, setCampaignId] = useState("");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState({ campaigns: true, products: false });
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        const res = await axiosInstance.get("/admin/campaign/get-all");
        const list = res.data.campaigns || [];
        setCampaigns(list);
        if (list.length > 0) setCampaignId(list[0].id);
      } catch (err) {
        setError(err?.response?.data?.error || "Failed to load campaigns");
      } finally {
        setLoading((prev) => ({ ...prev, campaigns: false }));
      }
    };
    fetchCampaigns();
  }, []);

  const fetchProducts = async (id) => {
    setLoading((prev) => ({ ...prev, products: true }));
    try {
      const res = await axiosInstance.get(
        `/admin/campaign/product/get-by-campaign/${id}`,
      );
      setProducts(res.data.products || []);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to load products");
    } finally {
      setLoading((prev) => ({ ...prev, products: false }));
    }
  };

  useEffect(() => {
    if (campaignId) fetchProducts(campaignId);
  }, [campaignId]);

  const handleCreated = (product) => {
    setProducts((prev) => [product, ...prev]);
    setModalOpen(false);
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Products</h1>
          <p className="mt-1 text-sm text-gray-500">
            Products attached to a campaign.
          </p>
        </div>
        <button
          onClick={() => setModalOpen(true)}
          disabled={!campaignId}
          className="rounded-md px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          style={{
            background: `linear-gradient(90deg, ${ACCENT_FROM}, ${ACCENT_TO})`,
          }}
        >
          + New product
        </button>
      </div>

      <div className="mt-4">
        <label className="block text-sm font-medium text-gray-700">
          Campaign
        </label>
        <select
          value={campaignId}
          onChange={(e) => setCampaignId(e.target.value)}
          disabled={loading.campaigns}
          className="mt-1 w-full max-w-sm rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2"
          style={{ "--tw-ring-color": ACCENT_FROM }}
        >
          {loading.campaigns && <option>Loading campaigns...</option>}
          {!loading.campaigns && campaigns.length === 0 && (
            <option>No campaigns yet</option>
          )}
          {campaigns.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      <div className="mt-6 overflow-hidden rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Product
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Category
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Original
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Discounted
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                MOQ
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {loading.products && (
              <tr>
                <td
                  colSpan={5}
                  className="px-4 py-6 text-center text-sm text-gray-400"
                >
                  Loading...
                </td>
              </tr>
            )}
            {!loading.products && error && (
              <tr>
                <td
                  colSpan={5}
                  className="px-4 py-6 text-center text-sm text-red-600"
                >
                  {error}
                </td>
              </tr>
            )}
            {!loading.products && !error && products.length === 0 && (
              <tr>
                <td
                  colSpan={5}
                  className="px-4 py-6 text-center text-sm text-gray-400"
                >
                  No products yet.
                </td>
              </tr>
            )}
            {!loading.products &&
              !error &&
              products.slice(0, 10).map((p) => (
                <tr key={p.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm">
                    <div className="flex items-center gap-3">
                      {p.productImg && (
                        <img
                          src={p.productImg}
                          alt=""
                          className="h-10 w-10 rounded object-cover border border-gray-200"
                        />
                      )}
                      <span className="font-medium text-gray-900 line-clamp-1 max-w-xs">
                        {p.productTitle}
                      </span>
                    </div>
                  </td>
                  {/* Requires campaignCategory (and its segment) to be included by the backend — see note below */}
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {p.campaignCategory?.name || "—"}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    ৳{(p.priceOriginal * p.fxRate).toFixed(2) ?? "—"}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    ৳{(p.priceDiscounted * p.fxRate).toFixed(2) ?? "—"}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {p.moq ?? "—"}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title="New product"
      >
        <CreateCampaignProductForm
          campaignId={campaignId}
          onCreated={handleCreated}
          onCancel={() => setModalOpen(false)}
        />
      </Modal>
    </div>
  );
}
